import { motion } from 'framer-motion';
import { Settings, Shield, Save, RefreshCcw } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { kycApi } from '@/lib/api';

// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.1 },
    },
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
};

const documentTypes = ['Aadhaar Card', 'PAN Card', 'Driving License', 'Passport'];

export default function KYCSettings() {
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [requireDocuments, setRequireDocuments] = useState(true);
    const [notifyOnSubmission, setNotifyOnSubmission] = useState(true);
    const [acceptedDocumentTypes, setAcceptedDocumentTypes] = useState<string[]>(['Aadhaar Card', 'PAN Card', 'Driving License']);
    const [maxReviewTimeDays, setMaxReviewTimeDays] = useState(7);
    const [minimumAge, setMinimumAge] = useState(18);
    const [reverificationPeriodMonths, setReverificationPeriodMonths] = useState(12);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            setIsLoading(true);
            const response = await kycApi.getKYCSettings();
            const data = response.data.data;
            
            setRequireDocuments(data.requireDocuments !== undefined ? data.requireDocuments : true);
            setNotifyOnSubmission(data.notifyOnSubmission !== undefined ? data.notifyOnSubmission : true);
            setAcceptedDocumentTypes(data.acceptedDocumentTypes || ['Aadhaar Card', 'PAN Card', 'Driving License']);
            setMaxReviewTimeDays(data.maxReviewTimeDays || 7);
            setMinimumAge(data.minimumAge || 18);
            setReverificationPeriodMonths(data.reverificationPeriodMonths || 12);
        } catch (error: any) {
            console.error('Failed to fetch KYC settings:', error);
            toast.error(error.response?.data?.message || 'Failed to load KYC settings');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            setIsSaving(true);
            await kycApi.updateKYCSettings({
                requireDocuments,
                notifyOnSubmission,
                acceptedDocumentTypes,
                maxReviewTimeDays,
                minimumAge,
                reverificationPeriodMonths,
            });
            toast.success('KYC settings saved successfully');
        } catch (error: any) {
            console.error('Failed to save KYC settings:', error);
            toast.error(error.response?.data?.message || 'Failed to save KYC settings');
        } finally {
            setIsSaving(false);
        }
    };

    const handleReset = () => {
        fetchSettings();
        toast.success('Settings reset to saved values');
    };

    const toggleDocumentType = (docType: string) => {
        setAcceptedDocumentTypes(prev => 
            prev.includes(docType)
                ? prev.filter(t => t !== docType)
                : [...prev, docType]
        );
    };

    if (isLoading) {
        return (
            <div className="flex h-[60vh] w-full items-center justify-center rounded-xl bg-slate-900/50 border border-slate-800">
                <div className="h-10 w-10 animate-spin rounded-full border-4 border-violet-500 border-t-transparent" />
            </div>
        );
    }

    return (
        <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-6"
        >
            {/* Header */}
            <motion.div variants={itemVariants} className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-3">
                        <Shield className="w-7 h-7 text-violet-400" />
                        KYC Settings
                    </h1>
                    <p className="text-slate-400 mt-1">Configure KYC verification settings</p>
                </div>
                <div className="flex items-center gap-3">
                    <Button variant="outline" onClick={handleReset} disabled={isSaving}>
                        <RefreshCcw className="w-4 h-4 mr-2" />
                        Reset
                    </Button>
                    <Button variant="default" onClick={handleSave} disabled={isSaving} isLoading={isSaving}>
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                    </Button>
                </div>
            </motion.div>

            {/* Settings Cards */}
            <motion.div variants={itemVariants} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* General Settings */}
                <Card variant="glass" className="p-6">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <Settings className="w-5 h-5 text-violet-400" />
                        General Settings
                    </h3>
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="font-medium">Require document uploads</p>
                                <p className="text-sm text-slate-400">Users must upload ID documents for verification</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={requireDocuments}
                                    onChange={(e) => setRequireDocuments(e.target.checked)}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-violet-500/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
                            </label>
                        </div>

                        <div className="flex items-center justify-between">
                            <div>
                                <p className="font-medium">Notify on new submission</p>
                                <p className="text-sm text-slate-400">Send notifications when users submit KYC</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={notifyOnSubmission}
                                    onChange={(e) => setNotifyOnSubmission(e.target.checked)}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-violet-500/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
                            </label>
                        </div>
                    </div>
                </Card>

                {/* Document Requirements */}
                <Card variant="glass" className="p-6">
                    <h3 className="text-lg font-semibold mb-4">Document Requirements</h3>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium mb-2">Accepted Document Types</label>
                            <div className="space-y-2">
                                {documentTypes.map((docType) => (
                                    <label key={docType} className="flex items-center gap-2 text-sm cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={acceptedDocumentTypes.includes(docType)}
                                            onChange={() => toggleDocumentType(docType)}
                                            className="rounded border-slate-600 bg-slate-700 text-violet-600 focus:ring-violet-500"
                                        />
                                        <span>{docType}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                </Card>
            </motion.div>

            {/* Additional Settings */}
            <motion.div variants={itemVariants}>
                <Card variant="glass" className="p-6">
                    <h3 className="text-lg font-semibold mb-4">Verification Thresholds</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-2">Maximum Review Time (days)</label>
                            <input
                                type="number"
                                value={maxReviewTimeDays}
                                onChange={(e) => setMaxReviewTimeDays(parseInt(e.target.value) || 0)}
                                min="1"
                                className="w-full px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2">Minimum Age Requirement</label>
                            <input
                                type="number"
                                value={minimumAge}
                                onChange={(e) => setMinimumAge(parseInt(e.target.value) || 0)}
                                min="1"
                                className="w-full px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2">Re-verification Period (months)</label>
                            <input
                                type="number"
                                value={reverificationPeriodMonths}
                                onChange={(e) => setReverificationPeriodMonths(parseInt(e.target.value) || 0)}
                                min="1"
                                className="w-full px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                            />
                        </div>
                    </div>
                </Card>
            </motion.div>
        </motion.div>
    );
}
